import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Alert,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    Event as EventIcon
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    collection,
    addDoc,
    getDocs,
    deleteDoc,
    updateDoc,
    doc,
    serverTimestamp,
    query,
    orderBy
} from "firebase/firestore";
import { db } from "../firebase";

const BlockedDatesManager = ({ orgId }) => {
    const [blockedDates, setBlockedDates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDate, setEditingDate] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "info" });

    const [formData, setFormData] = useState({
        startDate: null,
        endDate: null,
        reason: '',
        type: 'company_holiday',
        isRecurring: false,
        affectsAllEmployees: true,
        departments: []
    });

    const blockedDateTypes = [
        { value: 'company_holiday', label: 'Company Holiday' },
        { value: 'blackout_period', label: 'Blackout Period' },
        { value: 'maintenance', label: 'System Maintenance' },
        { value: 'training', label: 'Mandatory Training' },
        { value: 'inventory', label: 'Inventory Period' },
        { value: 'other', label: 'Other' }
    ];

    useEffect(() => {
        if (orgId) {
            fetchBlockedDates();
        }
    }, [orgId]);

    const fetchBlockedDates = async () => {
        setLoading(true);
        try {
            const blockedDatesRef = collection(db, "organizations", orgId, "blockedDates");
            const q = query(blockedDatesRef, orderBy("startDate", "asc"));
            const snapshot = await getDocs(q);

            const dates = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                startDate: doc.data().startDate?.toDate?.() || new Date(doc.data().startDate),
                endDate: doc.data().endDate?.toDate?.() || new Date(doc.data().endDate),
                createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
            }));

            setBlockedDates(dates);
        } catch (error) {
            console.error("Error fetching blocked dates:", error);
            setAlert({
                open: true,
                message: "Failed to load blocked dates",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (dateToEdit = null) => {
        if (dateToEdit) {
            setEditingDate(dateToEdit);
            setFormData({
                startDate: dateToEdit.startDate,
                endDate: dateToEdit.endDate,
                reason: dateToEdit.reason || '',
                type: dateToEdit.type || 'company_holiday',
                isRecurring: dateToEdit.isRecurring || false,
                affectsAllEmployees: dateToEdit.affectsAllEmployees !== false,
                departments: dateToEdit.departments || []
            });
        } else {
            setEditingDate(null);
            setFormData({
                startDate: null,
                endDate: null,
                reason: '',
                type: 'company_holiday',
                isRecurring: false,
                affectsAllEmployees: true,
                departments: []
            });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingDate(null);
        setFormData({
            startDate: null,
            endDate: null,
            reason: '',
            type: 'company_holiday',
            isRecurring: false,
            affectsAllEmployees: true,
            departments: []
        });
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = () => {
        if (!formData.startDate) {
            setAlert({
                open: true,
                message: "Please select a start date",
                severity: "error"
            });
            return false;
        }

        if (!formData.endDate) {
            setAlert({
                open: true,
                message: "Please select an end date",
                severity: "error"
            });
            return false;
        }

        if (formData.startDate > formData.endDate) {
            setAlert({
                open: true,
                message: "Start date cannot be after end date",
                severity: "error"
            });
            return false;
        }

        if (!formData.reason.trim()) {
            setAlert({
                open: true,
                message: "Please provide a reason for blocking these dates",
                severity: "error"
            });
            return false;
        }

        return true;
    };

    const handleSaveBlockedDate = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const blockedDateData = {
                startDate: formData.startDate,
                endDate: formData.endDate,
                reason: formData.reason,
                type: formData.type,
                isRecurring: formData.isRecurring,
                affectsAllEmployees: formData.affectsAllEmployees,
                departments: formData.departments,
                updatedAt: serverTimestamp()
            };

            if (editingDate) {
                // Update existing blocked date
                const blockedDateRef = doc(db, "organizations", orgId, "blockedDates", editingDate.id);
                await updateDoc(blockedDateRef, blockedDateData);

                setAlert({
                    open: true,
                    message: "Blocked date updated successfully",
                    severity: "success"
                });
            } else {
                // Create new blocked date
                blockedDateData.createdAt = serverTimestamp();

                const blockedDatesRef = collection(db, "organizations", orgId, "blockedDates");
                await addDoc(blockedDatesRef, blockedDateData);

                setAlert({
                    open: true,
                    message: "Blocked date created successfully",
                    severity: "success"
                });
            }

            handleCloseDialog();
            fetchBlockedDates();
        } catch (error) {
            console.error("Error saving blocked date:", error);
            setAlert({
                open: true,
                message: "Failed to save blocked date",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteBlockedDate = async (dateId) => {
        if (!window.confirm("Are you sure you want to delete this blocked date?")) {
            return;
        }

        setLoading(true);
        try {
            const blockedDateRef = doc(db, "organizations", orgId, "blockedDates", dateId);
            await deleteDoc(blockedDateRef);

            setAlert({
                open: true,
                message: "Blocked date deleted successfully",
                severity: "success"
            });

            fetchBlockedDates();
        } catch (error) {
            console.error("Error deleting blocked date:", error);
            setAlert({
                open: true,
                message: "Failed to delete blocked date",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate).toLocaleDateString();
        const end = new Date(endDate).toLocaleDateString();
        return start === end ? start : `${start} - ${end}`;
    };

    const getTypeColor = (type) => {
        const colors = {
            'company_holiday': 'success',
            'blackout_period': 'error',
            'maintenance': 'warning',
            'training': 'info',
            'inventory': 'secondary',
            'other': 'default'
        };
        return colors[type] || 'default';
    };

    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">
                        <BlockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Blocked Dates Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Add Blocked Date
                    </Button>
                </Box>

                {/* Alert */}
                {alert.open && (
                    <Alert severity={alert.severity} onClose={handleCloseAlert} sx={{ mb: 2 }}>
                        {alert.message}
                    </Alert>
                )}

                {/* Blocked Dates List */}
                <Card>
                    <CardContent>
                        {blockedDates.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <EventIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    No blocked dates configured
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Add blocked dates to prevent employees from requesting time off on specific days.
                                </Typography>
                            </Box>
                        ) : (
                            <List>
                                {blockedDates.map((blockedDate, index) => (
                                    <React.Fragment key={blockedDate.id}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle1">
                                                            {formatDateRange(blockedDate.startDate, blockedDate.endDate)}
                                                        </Typography>
                                                        <Chip
                                                            label={blockedDateTypes.find(t => t.value === blockedDate.type)?.label || blockedDate.type}
                                                            color={getTypeColor(blockedDate.type)}
                                                            size="small"
                                                        />
                                                        {blockedDate.isRecurring && (
                                                            <Chip
                                                                label="Recurring"
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {blockedDate.reason}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Affects: {blockedDate.affectsAllEmployees ? 'All employees' : 'Specific departments'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpenDialog(blockedDate)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleDeleteBlockedDate(blockedDate.id)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < blockedDates.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </CardContent>
                </Card>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>
                        {editingDate ? 'Edit Blocked Date' : 'Add Blocked Date'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <Grid container spacing={2}>
                                {/* Date Range */}
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Start Date"
                                        value={formData.startDate}
                                        onChange={(newValue) => handleFormChange('startDate', newValue)}
                                        slotProps={{
                                            textField: { fullWidth: true }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="End Date"
                                        value={formData.endDate}
                                        onChange={(newValue) => handleFormChange('endDate', newValue)}
                                        slotProps={{
                                            textField: { fullWidth: true }
                                        }}
                                    />
                                </Grid>

                                {/* Type */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Type</InputLabel>
                                        <Select
                                            value={formData.type}
                                            onChange={(e) => handleFormChange('type', e.target.value)}
                                            label="Type"
                                        >
                                            {blockedDateTypes.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {type.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Switches */}
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ pt: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isRecurring}
                                                    onChange={(e) => handleFormChange('isRecurring', e.target.checked)}
                                                />
                                            }
                                            label="Recurring annually"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.affectsAllEmployees}
                                                    onChange={(e) => handleFormChange('affectsAllEmployees', e.target.checked)}
                                                />
                                            }
                                            label="Affects all employees"
                                            sx={{ display: 'block' }}
                                        />
                                    </Box>
                                </Grid>

                                {/* Reason */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Reason"
                                        value={formData.reason}
                                        onChange={(e) => handleFormChange('reason', e.target.value)}
                                        multiline
                                        rows={3}
                                        placeholder="Explain why these dates are blocked (e.g., Christmas Holiday, Year-end inventory, etc.)"
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveBlockedDate}
                            variant="contained"
                            disabled={loading}
                        >
                            {editingDate ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default BlockedDatesManager;